<template>
  <div>
    <!-- 文件型（Word/PDF这种），走自定义展示：文件名可点开查看，不是缩略图 -->
    <div v-if="listType === 'file'" class="file-upload">
      <div v-if="!fileState.url && !fileState.uploading" class="file-upload-btn" @click="chooseFile">
        <van-icon name="description" size="20" />
        <span>点击上传</span>
      </div>
      <div v-else-if="fileState.uploading" class="file-upload-doing">
        <van-loading size="16" />
        <span>上传中...{{ fileState.name }}</span>
      </div>
      <div v-else class="file-upload-done">
        <van-icon name="description" size="18" color="#0E7490" />
        <a :href="fileState.url" target="_blank" class="file-name">{{ fileState.name }}</a>
        <van-icon name="cross" size="16" color="#94A3B8" @click="removeFile" />
      </div>
      <input
        ref="fileInput"
        type="file"
        :accept="accept"
        style="display: none"
        @change="onFileChange"
      />
    </div>

    <!-- 图片型（头像这种），保留原来的van-uploader缩略图展示 -->
    <van-uploader
      v-else
      :file-list="fileList"
      :after-read="afterRead"
      :max-count="maxCount"
      :accept="accept"
      @delete="onDelete"
    />
  </div>
</template>

<script>
import { Uploader, Toast, Icon, Loading } from "vant";
import axios from "axios";
import { getOssKey } from "@/api/oss.js";

export default {
  name: "Upload",
  components: {
    [Uploader.name]: Uploader,
    [Icon.name]: Icon,
    [Loading.name]: Loading,
  },
  props: {
    // OSS目录，格式跟管理后台一样：一级目录必须在后端白名单里(比如 user/xxx)
    dir: {
      type: String,
      default: "user/nrc",
    },
    maxCount: {
      type: Number,
      default: 1,
    },
    accept: {
      type: String,
      default: "image/*",
    },
    fileSizeLimitK: {
      type: Number,
      default: 5120,
    },
    // 'image': 缩略图展示(默认，给头像这种用)；'file': 文件名展示(给Word/PDF这种非图片文件用)
    listType: {
      type: String,
      default: "image",
    },
    // 允许的扩展名列表，比如 ['doc','docx','pdf']，不传则不做扩展名校验
    allowExt: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      fileList: [],
      fileState: {
        uploading: false,
        name: "",
        url: "",
      },
    };
  },
  methods: {
    chooseFile() {
      this.$refs.fileInput.value = "";
      this.$refs.fileInput.click();
    },
    onFileChange(e) {
      let file = e.target.files[0];
      if (!file) return;
      this.doUpload(file);
    },
    removeFile() {
      this.fileState = { uploading: false, name: "", url: "" };
      this.$emit("delete");
    },
    checkExt(fileName) {
      if (!this.allowExt.length) return true;
      let ext = (fileName.split(".").pop() || "").toLowerCase();
      return this.allowExt.map((e) => e.toLowerCase()).includes(ext);
    },
    doUpload(rawFile) {
      if (!this.checkExt(rawFile.name)) {
        Toast(`只支持上传 ${this.allowExt.join("/")} 格式的文件`);
        return;
      }
      if (rawFile.size / 1024 > this.fileSizeLimitK) {
        Toast(`文件大小超出${this.fileSizeLimitK}K限制`);
        return;
      }
      this.fileState = { uploading: true, name: rawFile.name, url: "" };
      this.$emit("uploadStart");

      let ext = (rawFile.name.split(".").pop() || "dat").toLowerCase();
      let mediaType = 3; // 文档类

      getOssKey({ path: this.dir, mediaType })
        .then((r) => {
          let fileName = r.fileName + "." + ext;
          let key = r.dir + "/" + fileName;
          let fullUrl = r.host + "/" + r.dir + "/" + fileName;

          let formData = new FormData();
          formData.append("name", fileName);
          formData.append("key", key);
          formData.append("policy", r.policy);
          formData.append("OSSAccessKeyId", r.accessId);
          formData.append("signature", r.signature);
          formData.append("success_action_status", "200");
          formData.append("file", rawFile);

          return axios.post(r.host, formData).then(() => fullUrl);
        })
        .then((fullUrl) => {
          this.fileState = { uploading: false, name: rawFile.name, url: fullUrl };
          this.$emit("uploadSuccess", fullUrl, rawFile.name);
        })
        .catch(() => {
          this.fileState = { uploading: false, name: "", url: "" };
          Toast("上传失败，请重试");
          this.$emit("uploadFail");
        });
    },
    // 下面这些是原来给图片(van-uploader)用的逻辑，保持不变
    afterRead(file) {
      if (!this.checkExt(file.file.name)) {
        Toast(`只支持上传 ${this.allowExt.join("/")} 格式的文件`);
        this.fileList = this.fileList.filter((f) => f !== file);
        return;
      }
      if (file.file.size / 1024 > this.fileSizeLimitK) {
        Toast(`文件大小超出${this.fileSizeLimitK}K限制`);
        this.fileList = this.fileList.filter((f) => f !== file);
        return;
      }
      file.status = "uploading";
      file.message = "上传中...";
      this.$emit("uploadStart");

      let ext = (file.file.name.split(".").pop() || "dat").toLowerCase();
      let mediaType = /image/.test(file.file.type) ? 1 : /video/.test(file.file.type) ? 2 : 3;

      getOssKey({ path: this.dir, mediaType })
        .then((r) => {
          let fileName = r.fileName + "." + ext;
          let key = r.dir + "/" + fileName;
          let fullUrl = r.host + "/" + r.dir + "/" + fileName;

          let formData = new FormData();
          formData.append("name", fileName);
          formData.append("key", key);
          formData.append("policy", r.policy);
          formData.append("OSSAccessKeyId", r.accessId);
          formData.append("signature", r.signature);
          formData.append("success_action_status", "200");
          formData.append("file", file.file);

          return axios.post(r.host, formData).then(() => fullUrl);
        })
        .then((fullUrl) => {
          file.status = "done";
          file.message = "";
          file.url = fullUrl;
          this.$emit("uploadSuccess", fullUrl, file.file.name);
        })
        .catch(() => {
          file.status = "failed";
          file.message = "上传失败";
          this.$emit("uploadFail");
        });
    },
    onDelete(file) {
      this.$emit("delete", file);
    },
  },
};
</script>

<style scoped>
.file-upload-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  border: 1px dashed var(--color-border, #e2e8f0);
  border-radius: 8px;
  color: var(--color-text-secondary, #475569);
  font-size: 13px;
  width: fit-content;
}
.file-upload-doing {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  color: var(--color-text-secondary, #475569);
  font-size: 13px;
}
.file-upload-done {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  background: var(--color-primary-bg, #ecfeff);
  border-radius: 8px;
  width: fit-content;
  max-width: 100%;
}
.file-name {
  color: var(--color-link, #0284c7);
  font-size: 13px;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
